<script lang="ts">
  /**
   * CelebrationScreen - Success screen after completing a word
   * Shows congratulations, score, and navigation options
   */

  import { onMount } from 'svelte';
  import { fade, scale } from 'svelte/transition';
  import { cubicOut } from 'svelte/easing';
  import StarField from '../ui/StarField.svelte';
  import ParticleSystem from '../effects/ParticleSystem.svelte';

  // Props
  interface Props {
    word: string;
    emoji: string;
    score: number;
    onPlayAgain?: () => void;
    onChangeMode?: () => void;
    onHome?: () => void;
  }

  let {
    word,
    emoji,
    score,
    onPlayAgain = () => {},
    onChangeMode = () => {},
    onHome = () => {}
  }: Props = $props();

  // Random celebration messages
  const messages = [
    'FANTASTIC JOB!',
    "YOU'RE AMAZING!",
    'WOW! GREAT WORK!',
    'EXCELLENT!',
    'SUPERSTAR!'
  ];
  const message = messages[Math.floor(Math.random() * messages.length)];

  // Particle system reference
  let particleSystem: any;

  // Trigger confetti on mount
  onMount(() => {
    if (particleSystem) {
      // Start confetti celebration - 5 seconds of continuous confetti
      particleSystem.createConfetti(5000);
    }
  });
</script>

<div class="celebration-screen">
  <StarField />

  <div class="content" transition:fade={{ duration: 400, easing: cubicOut }}>
    <!-- Celebration Message -->
    <h1 class="message" transition:scale={{ duration: 600, delay: 200, easing: cubicOut }}>
      {message}
    </h1>

    <!-- Completed Word Display -->
    <div class="word-display" transition:scale={{ duration: 500, delay: 400, easing: cubicOut }}>
      <div class="word-emoji">{emoji}</div>
      <div class="word-text">{word}</div>
    </div>

    <!-- Score Display -->
    <div class="score-container" transition:scale={{ duration: 500, delay: 600, easing: cubicOut }}>
      <div class="score-label">Total Points</div>
      <div class="score-value">{score}</div>
    </div>

    <!-- Action Buttons -->
    <div class="buttons" transition:fade={{ duration: 400, delay: 800 }}>
      <button class="action-button primary" onclick={onPlayAgain}>
        <span class="icon">🔄</span>
        Play Again
      </button>
      <button class="action-button secondary" onclick={onChangeMode}>
        <span class="icon">🎮</span>
        Change Mode
      </button>
      <button class="action-button secondary" onclick={onHome}>
        <span class="icon">🏠</span>
        Home
      </button>
    </div>
  </div>

  <!-- Particle System for Confetti -->
  <ParticleSystem bind:this={particleSystem} />
</div>

<style>
  .celebration-screen {
    position: relative;
    width: 100vw;
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .content {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-xl);
    padding: var(--space-lg);
    width: 100%;
    max-width: 600px;
  }

  /* Celebration Message */
  .message {
    font-size: 64px;
    font-weight: bold;
    color: var(--color-bright-yellow);
    margin: 0;
    text-align: center;
    text-shadow: 0 0 30px rgba(252, 211, 77, 0.8);
    animation: pulse 1.5s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
  }

  /* Word Display */
  .word-display {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-md);
    padding: var(--space-xl);
    background: rgba(10, 22, 40, 0.9);
    border-radius: var(--radius-lg);
    border: 3px solid var(--color-bright-yellow);
    box-shadow: 0 0 40px rgba(252, 211, 77, 0.3);
  }

  .word-emoji {
    font-size: 120px;
    filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.5));
  }

  .word-text {
    font-size: 48px;
    font-weight: bold;
    color: var(--color-bright-yellow);
    letter-spacing: 12px;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  }

  /* Score Display */
  .score-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-sm);
  }

  .score-label {
    font-size: var(--font-lg);
    color: var(--color-text-secondary);
    font-weight: 600;
  }

  .score-value {
    font-size: 72px;
    font-weight: bold;
    color: var(--color-bright-yellow);
    text-shadow: 0 0 20px rgba(252, 211, 77, 0.6);
  }

  /* Action Buttons */
  .buttons {
    display: flex;
    flex-direction: column;
    gap: var(--space-md);
    width: 100%;
    max-width: 400px;
  }

  .action-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-md);
    padding: var(--space-lg) var(--space-xl);
    border: 3px solid transparent;
    border-radius: var(--radius-md);
    font-size: var(--font-lg);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  .action-button .icon {
    font-size: 28px;
  }

  .action-button.primary {
    background: linear-gradient(135deg, #10B981 0%, #86EFAC 100%);
    color: white;
  }

  .action-button.primary:hover {
    border-color: #86EFAC;
    transform: scale(1.03);
    box-shadow: 0 0 30px rgba(134, 239, 172, 0.5);
  }

  .action-button.secondary {
    background: var(--color-space-blue);
    color: white;
  }

  .action-button.secondary:hover {
    background: var(--color-cosmic-purple);
    transform: scale(1.03);
  }

  .action-button:active {
    transform: scale(0.98);
  }

  /* Responsive */
  @media (max-width: 767px) {
    .message {
      font-size: 40px;
    }

    .word-emoji {
      font-size: 80px;
    }

    .word-text {
      font-size: 32px;
      letter-spacing: 6px;
    }

    .score-value {
      font-size: 56px;
    }

    .action-button {
      padding: var(--space-md) var(--space-lg);
      font-size: var(--font-body);
    }

    .action-button .icon {
      font-size: 24px;
    }
  }
</style>
